import React from 'react'
import { Button, Flex, H2, Line } from '../../lib/components'
import type { ComingSoonAdminRow } from '../../services/coming-soon-admin-service'
import './coming-soon-list.scss'

export type ComingSoonListPresenterProps = {
  items: ComingSoonAdminRow[]
  isLoading: boolean
  onAdd: () => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
}

function truncateDescription(text: string, maxLen: number) {
  if (text.length <= maxLen) return text
  return `${text.slice(0, maxLen)}…`
}

export function ComingSoonListPresenter({
  items,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
}: ComingSoonListPresenterProps) {
  return (
    <Flex variant='column-center'>
      <div className='coming-soon-list w-100'>
        <div className='coming-soon-list__header'>
          <H2 text='Coming soon opportunities' />
          <Button
            text='Add'
            variant='basic-btn'
            handleClick={onAdd}
            classes={['coming-soon-list__add-btn']}
            dataTestId='coming-soon-list-add'
          />
        </div>
        <Line />
        {isLoading ? (
          <p className='coming-soon-list__loading'>Loading…</p>
        ) : items.length === 0 ? (
          <p className='coming-soon-list__empty'>
            No items yet. Use Add to create a preview for the public site.
          </p>
        ) : (
          <div className='coming-soon-list__table-wrap'>
            <table className='coming-soon-list__table'>
              <colgroup>
                <col style={{ width: '56px' }} />
                <col style={{ width: '22%' }} />
                <col />
                <col style={{ width: '72px' }} />
                <col style={{ width: '108px' }} />
                <col style={{ width: '188px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope='col'>ID</th>
                  <th scope='col'>Title</th>
                  <th scope='col'>Description</th>
                  <th scope='col'>Sort</th>
                  <th scope='col'>Published</th>
                  <th scope='col' className='coming-soon-list__th--actions'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td className='coming-soon-list__cell--id'>{row.id}</td>
                    <td className='coming-soon-list__cell--title'>
                      {truncateDescription(row.title || '—', 80)}
                    </td>
                    <td className='coming-soon-list__cell--desc'>
                      {truncateDescription(row.description, 140)}
                    </td>
                    <td className='coming-soon-list__cell--sort'>{row.sort_order}</td>
                    <td className='coming-soon-list__cell--published'>
                      <span
                        className={
                          row.is_published
                            ? 'coming-soon-list__badge coming-soon-list__badge--yes'
                            : 'coming-soon-list__badge coming-soon-list__badge--no'
                        }
                      >
                        {row.is_published ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <div className='coming-soon-list__actions'>
                        <Button
                          text='Edit'
                          variant='secondary-btn'
                          handleClick={() => onEdit(row.id)}
                          classes={['coming-soon-list__action-btn']}
                          dataTestId={`coming-soon-edit-${row.id}`}
                        />
                        <Button
                          text='Delete'
                          variant='secondary-btn'
                          handleClick={() => onDelete(row.id)}
                          classes={['coming-soon-list__action-btn']}
                          dataTestId={`coming-soon-delete-${row.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Flex>
  )
}
